import React, { useEffect, ChangeEvent } from "react";
import "./App.css";
import {
  AppShell,
  Field,
  Form,
  FormLayout,
  LoadingSpinner,
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  Select,
  SelectButton,
  SelectList,
  useSnackbar,
} from "@saas-ui/react";
import { FileUploader } from "./components/FileUploader";

import {
  Button,
  Box,
  Container,
  Heading,
  Switch,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Stack,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { ReactTagManager } from "react-gtm-ts";

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

// Type definitions for configurations
type ButtonConfig = {
  position: string;
  style: number;
  icon: string;
  backgroundColor: string;
};

type InstanceConfig = {
  baseUrl: string;
  button: ButtonConfig;
  title: string;
  enableLabel: string;
  client_id: string;
};

type InstanceConfigs = {
  [key: string]: InstanceConfig;
};

// Default configurations for different instance types
const instanceConfigs: InstanceConfigs = {
  whatsapp: {
    baseUrl: "https://wix.certifiedcode.io/ecwid-whatsapp",
    button: {
      position: "right",
      style: 1,
      icon: '<i class="fab fa-whatsapp"></i>',
      backgroundColor: "#10c379",
    },
    title: "WhatsApp: Start Chat Button",
    enableLabel: "Enable WhatsApp?",
    client_id: "startchat-button",
  },
  messenger: {
    baseUrl: "https://wix.certifiedcode.io/ecwid-messenger",
    button: {
      position: "right",
      style: 1,
      icon: '<i class="fab fa-facebook-messenger"></i>',
      backgroundColor: "#0084ff",
    },
    title: "Messenger: Start Chat Button",
    enableLabel: "Enable Messenger?",
    client_id: "start-chat",
  },
  telegram: {
    baseUrl: "https://wix.certifiedcode.io/ecwid-telegram",
    button: {
      position: "right",
      style: 1,
      icon: '<i class="fa-brands fa-telegram"></i>',
      backgroundColor: "#18A3E6",
    },
    title: "Telegram: Start Chat Button",
    enableLabel: "Enable Telegram?",
    client_id: "telegram-start-chat-dev",
  },
};

// Export types and configurations
export type { ButtonConfig, InstanceConfig, InstanceConfigs };
export { instanceConfigs };

// Ensure this file is treated as a module
export {};

// Add this interface before the App component
interface StoreData {
  store_id?: string;
  subscription?: {
    management_urls: {
      update_payment_method: string;
    };
  };
  storeProfile?: {
    account: {
      accountEmail: string;
      accountName: string;
      registrationDate: string;
    };
    company: {
      countryCode: string;
      postalCode: string;
      city: string;
      phone: string;
    };
  };
  intercomToken?: string;
  app?: {
    client_id: string;
  };
}

// Add this function before the App component
function getCheckoutUrl(storeData: StoreData): string | undefined {
  if (!storeData?.storeProfile) return undefined;
  
  return encodeURI(
    "https://www.certifiedcode.us/upgrade/apps?checkout=" +
      JSON.stringify({
        settings: {
          allowLogout: false,
          allowDiscountRemoval: false,
        },
        items: [
          {
            priceId: "pri_01jfc28m654z364s4nx0wpezdc",
            quantity: 1,
          },
        ],
        customer: {
          email: storeData.storeProfile.account.accountEmail,
          business: {
            name: storeData.storeProfile.account.accountName,
          },
          address: {
            countryCode: storeData.storeProfile.company.countryCode,
            postalCode: storeData.storeProfile.company.postalCode,
            city: storeData.storeProfile.company.city,
          },
        },
        customData: {
          store_id: `${storeData.store_id}`,
        },
      })
  );
}

// Add this validation function near the top of the file, after the type definitions
function hasTemplatePhoneNumber(config: any): boolean {
  const templatePatterns = [/3475086396/, /1234567890/];
  
  return config.popup.persons.some((person: any) => {
    return templatePatterns.some(pattern => 
      pattern.test(person.link.desktop) || 
      (person.link.mobile && pattern.test(person.link.mobile))
    );
  });
}

// Add this helper function after hasTemplatePhoneNumber
function findFirstInvalidPersonIndex(config: any): number {
  const templatePatterns = [/3475086396/, /1234567890/];
  
  return config.popup.persons.findIndex((person: any) => {
    return templatePatterns.some(pattern => 
      pattern.test(person.link.desktop) || 
      (person.link.mobile && pattern.test(person.link.mobile))
    );
  });
}

export function App() {
  ReactTagManager.init(tagManagerArgs);
  const snackbar = useSnackbar();

  const [userInitiated, setUserInitiated] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState(false);

  // Get instance from URL path
  const pathname = window.location.pathname;
  const [instanceName, setInstanceName] = React.useState(
    pathname.split("/").filter(Boolean)[0] || "whatsapp"
  );
  const instanceConfig =
    instanceConfigs[instanceName] || instanceConfigs["whatsapp"];
  const [isEnabled, setIsEnabled] = React.useState(true);
  const [storeData, setStoreData] = React.useState<StoreData>({});
  const [config, setConfig] = React.useState({
    /* Button Settings */
    button: {
      position: instanceConfig.button.position,
      style: instanceConfig.button.style,
      src: instanceConfig.button.icon,
      backgroudColor: instanceConfig.button.backgroundColor,
      effect: 1 /* Button effect. Number between 1 and 7 */,
      notificationNumber:
        "1" /* Custom text or false. To remove, (notificationNumber:false) */,
      speechBubble:
        "How can we help you?" /* To remove, (speechBubble:false) */,
      pulseEffect: true /* To remove, (pulseEffect:false) */,
      text: {
        /* For Button style larger than 1 */
        title: "Need help? Chat with us" /* Writing is required */,
        description: "Customer Support" /* To remove, (description:false) */,
        online: "I'm Online" /* To remove, (online:false) */,
        offline: "I will be back soon" /* To remove, (offline:false) */,
      },
    },

    /* Popup Settings */
    popup: {
      automaticOpen:
        false /* true or false (Open popup automatically when the page is loaded) */,
      outsideClickClosePopup:
        true /* true or false (Clicking anywhere on the page will close the popup) */,
      effect: 1 /* Popup opening effect. Number between 1 and 15 */,
      header: {
        backgroudColor:
          instanceConfig.button.backgroundColor /* Html color code */,
        title: "Need help? Chat with us" /* Writing is required */,
        description:
          "Click one of our representatives below" /* To remove, (description:false) */,
      },

      /* Representative Settings */
      persons: [
        /* Copy for more representatives [::Start Copy::] */
        {
          avatar: {
            src: '<img src="assets/img/person/1.jpg" alt="">' /* Image, Icon or SVG */,
            backgroudColor: "#ffffff" /* Html color code */,
            onlineCircle:
              true /* Avatar online circle. To remove, (onlineCircle:false) */,
          },
          text: {
            title: "Lorna Hensley" /* Writing is required */,
            description: "Sales Support" /* To remove, (description:false) */,
            online: "I'm Online" /* To remove, (online:false) */,
            offline: "I will be back soon" /* To remove, (offline:false) */,
          },
          link: {
            desktop:
              "https://web.whatsapp.com/send?phone=13475086396&text=Hi" /* Writing is required */,
            mobile:
              "https://wa.me/13475086396/?text=Hi" /* If it is hidden desktop link will be valid. To remove, (mobile:false) */,
          },
          onlineDay: {
            /* Change the day you are offline like this. (sunday:false) */
            sunday: "00:00-23:59",
            monday: "00:00-23:59",
            tuesday: "00:00-23:59",
            wednesday: "00:00-23:59",
            thursday: "00:00-23:59",
            friday: "00:00-23:59",
            saturday: "00:00-23:59",
          },
        },
        /* [::End Copy::] */

        /* Copy for more representatives [::Start Copy::] */
        {
          avatar: {
            src: '<img src="assets/img/person/2.jpg" alt="">' /* Font, Image or SVG */,
            backgroudColor: "#ffffff" /* Html color code */,
            onlineCircle:
              true /* Avatar online circle. To remove, (onlineCircle:false) */,
          },
          text: {
            title: "Mattie Simmonds" /* Writing is required */,
            description:
              "Customer Support" /* Custom text or false. To remove, (description:false) */,
            online:
              "I'm Online" /* Custom text or false. To remove, (online:false) */,
            offline:
              "I will be back soon" /* Custom text or false. To remove, (offline:false) */,
          },
          link: {
            desktop:
              "https://web.whatsapp.com/send?phone=13475086396&text=Hi" /* Writing is required */,
            mobile:
              "https://wa.me/13475086396/?text=Hi" /* If it is hidden desktop link will be valid. To remove, (mobile:false) */,
          },
          onlineDay: {
            /* Change the day you are offline like this. (sunday:false) */
            sunday: "00:00-23:59",
            monday: "00:00-23:59",
            tuesday: "00:00-23:59",
            wednesday: "00:00-23:59",
            thursday: "00:00-23:59",
            friday: "00:00-23:59",
            saturday: "00:00-23:59",
          },
        },
        /* [::End Copy::] */

        /* Copy for more representatives [::Start Copy::] */
        {
          avatar: {
            src: '<img src="assets/img/person/3.jpg" alt="">' /* Font, Image or SVG */,
            backgroudColor: "#ffffff" /* Html color code */,
            onlineCircle:
              true /* Avatar online circle. To remove, (onlineCircle:false) */,
          },
          text: {
            title: "Kole Cleg" /* Writing is required */,
            description:
              "Techincal Support" /* Custom text or false. To remove, (description:false) */,
            online:
              "I'm Online" /* Custom text or false. To remove, (online:false) */,
            offline:
              "I will be back soon" /* Custom text or false. To remove, (offline:false) */,
          },
          link: {
            desktop:
              "https://web.whatsapp.com/send?phone=13475086396&text=Hi" /* Writing is required */,
            mobile:
              "https://wa.me/13475086396/?text=Hi" /* If it is hidden desktop link will be valid. To remove, (mobile:false) */,
          },
          onlineDay: {
            /* Change the day you are offline like this. (sunday:false) */
            sunday: false,
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
          },
        },
        /* [::End Copy::] */
      ],
    },

    /* Other Settings */
    sound:
      true /* true (default sound), false or custom sound. Custom sound example, (sound:'assets/sound/notification.mp3') */,
    changeBrowserTitle:
      "New Message!" /* Custom text or false. To remove, (changeBrowserTitle:false) */,
    cookie:
      false /* It does not show the speech bubble, notification number, and pulse effect again for the specified time. For example, do not show for 1 hour, (cookie:1) or to remove, (cookie:false) */,
  } as any);

  // Add new state for upgrade modal
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = React.useState(false);

  useEffect(() => {
    try {
      // Parse instance name from URL path
      const pathParts = window.location.pathname.split("/");
      const currentInstance = pathParts[1] || "whatsapp";
      setInstanceName(currentInstance);
      console.log("Instance name:", currentInstance);

      // Verify fallback behavior for base URL
      if (!pathParts[1]) {
        console.log("No instance specified, using default: whatsapp");
      }

      // Load the correct default configuration
      const defaultConfig =
        instanceConfigs[currentInstance] || instanceConfigs["whatsapp"];
      setConfig((prevConfig: typeof config) => ({
        ...prevConfig,
        button: {
          ...prevConfig.button,
          ...defaultConfig.button,
        },
      }));

      // Initialize the application
      window.EcwidApp.init({
        app_id: instanceConfigs[currentInstance].client_id, // Use parsed instance name as app_id
        autoloadedflag: true,
        autoheight: false,
      });

      getInstance();
      getSettings();

      window.EcwidApp.setSize({ height: 1300 });

      const checker = setInterval(() => {
        getInstance();
      }, 10000);

      return () => {
        clearInterval(checker);
      };
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    console.log(config);
    if (config?.button?.style) {
      try {
        // remove existing chat element
        window.$("#whatsapp-preview").remove();
        // create new chat element to body
        window.$("body").append('<div id="whatsapp-preview"></div>');
        window.$("#whatsapp-preview").czmChatSupport(config);
      } catch (error) {
        console.error(error);
      }
    }
  }, [config]);

  // Auto-save effect with debouncing
  useEffect(() => {
    if (!config?.button?.style) return; // Don't save if config is not properly initialized
    if (!userInitiated) return; // Only save when changes are user-initiated

    const saveTimer = setTimeout(() => {
      setIsSaving(true);
      setSettings(isEnabled)
        .then(() => {
          setIsSaving(false);
          snackbar.success("Settings saved successfully");
          setUserInitiated(false); // Reset flag after successful save
        })
        .catch((error) => {
          console.error("Error saving settings:", error);
          setIsSaving(false);
          snackbar.error("Failed to save settings");
        });
    }, 500); // 500ms debounce

    return () => clearTimeout(saveTimer);
  }, [config, snackbar, userInitiated, isEnabled]);

  useEffect(() => {
    console.log(storeData);
    if (storeData.intercomToken && storeData.storeProfile && storeData.app) {
      const name = storeData.storeProfile.account.accountName;
      const registrationDate = new Date(storeData.storeProfile.account.registrationDate);
      const created_at = Math.floor(registrationDate.getTime() / 1000);
      const phone = storeData.storeProfile.company.phone;
      const email = storeData.storeProfile.account.accountEmail;
      const utm_source = storeData.app.client_id;
      const options = {
        email,
        name,
        created_at,
        phone,
        user_hash: storeData.intercomToken,
        utm_source,
      };
      window.Intercom("boot", options);
    }
  }, [storeData]);

  function setSettings(isEnabled?: boolean) {
    const baseUrl =
      instanceConfigs[instanceName]?.baseUrl ||
      instanceConfigs["whatsapp"].baseUrl;

    const defaultConfig = instanceConfigs[instanceName] || instanceConfigs["whatsapp"];

    // Only check phone numbers when enabling
    if (isEnabled) {
      const invalidPersonIndex = findFirstInvalidPersonIndex(config);
      if (invalidPersonIndex !== -1) {
        snackbar.error("Please update the template phone numbers before enabling");
        
        // Navigate to Popup Settings tab and expand the person's accordion
        const tabsEl = document.querySelector('[role="tablist"]');
        const popupSettingsTab = tabsEl?.children[1] as HTMLElement;
        if (popupSettingsTab) {
          // Add a highlight effect to the tab
          popupSettingsTab.style.transition = 'background-color 0.3s';
          popupSettingsTab.style.backgroundColor = '#FED7D7'; // Light red background
          popupSettingsTab.click();
          
          // Remove highlight after a delay
          setTimeout(() => {
            popupSettingsTab.style.backgroundColor = '';
          }, 2000);

          // Wait for tab panel to be rendered
          setTimeout(() => {
            const accordionButton = document.querySelectorAll('[data-accordion-button]')[invalidPersonIndex] as HTMLElement;
            if (accordionButton) {
              // Check if accordion is already expanded
              const accordionItem = accordionButton.closest('[role="region"]');
              const isExpanded = accordionItem?.getAttribute('aria-expanded') === 'true';
              
              if (!isExpanded) {
                accordionButton.click();
              }
              
              // Add highlight effect to the accordion button
              accordionButton.style.transition = 'background-color 0.3s';
              accordionButton.style.backgroundColor = '#FED7D7';
              
              // Scroll the person's section into view
              accordionButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
              
              // Remove highlight after a delay
              setTimeout(() => {
                accordionButton.style.backgroundColor = '';
              }, 2000);
            }
          }, 100);
        }

        return Promise.reject("Template phone numbers detected");
      }
    }

    // Ensure icon and color are always set from default settings
    const updatedConfig = {
      ...config,
      button: {
        ...config.button,
        icon: defaultConfig.button.icon,
        backgroundColor: defaultConfig.button.backgroundColor,
      },
      popup: {
        ...config.popup,
        header: {
          ...config.popup.header,
          backgroudColor: defaultConfig.button.backgroundColor,
        }
      }
    };

    return fetch(
      `${baseUrl}/_functions/@certifiedcode/ecwid-repluggable-backend/settings?instance=${instanceName}`,
      {
        method: "POST",
        headers: {
          Authorization: new URLSearchParams(window.location.search).get(
            "payload"
          )!,
        },
        body: JSON.stringify({
          isEnabled,
          config: updatedConfig,
        }),
      }
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        setConfig(data.config);
        setIsEnabled(data.isEnabled);
        return Promise.resolve();
      })
      .catch(() => {
        return Promise.reject();
      });
  }

  function getInstance() {
    const baseUrl =
      instanceConfigs[instanceName]?.baseUrl ||
      instanceConfigs["whatsapp"].baseUrl;
    fetch(
      `${baseUrl}/_functions/@certifiedcode/ecwid-repluggable-backend/auth?instance=${instanceName}`,
      {
        headers: {
          Authorization: new URLSearchParams(window.location.search).get(
            "payload"
          )!,
        },
      }
    )
      .then((response) => response.json())
      .then((payload) => {
        setStoreData(payload);
        window.EcwidApp.ready();
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  function getSettings() {
    const baseUrl =
      instanceConfigs[instanceName]?.baseUrl ||
      instanceConfigs["whatsapp"].baseUrl;
    fetch(
      `${baseUrl}/_functions/@certifiedcode/ecwid-repluggable-backend/settings?instance=${instanceName}`,
      {
        method: "GET",
        headers: {
          Authorization: new URLSearchParams(window.location.search).get(
            "payload"
          )!,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        setIsEnabled(data.isEnabled);
        // Get the default configuration for current instance
        const defaultConfig =
          instanceConfigs[instanceName] || instanceConfigs["whatsapp"];

        if (data.config) {
          // Merge the backend config with instance-specific defaults
          const mergedConfig = {
            ...data.config,
            button: {
              ...defaultConfig.button,
              ...data.config.button,
            },
          };
          setConfig(mergedConfig);
        } else {
          // If no config exists, use instance defaults
          setConfig((prevConfig: typeof config) => ({
            ...prevConfig,
            button: {
              ...prevConfig.button,
              ...defaultConfig.button,
            },
          }));
        }
      })
      .catch(() => {});
  }

  return (
    <AppShell
      navbar={
        <Navbar>
          <NavbarBrand>
            <Heading size="md">{instanceConfig.title}</Heading>
          </NavbarBrand>
          <NavbarContent justifyContent="flex-end" spacing="2">
            <NavbarItem>
              <Switch
                isChecked={isEnabled}
                onChange={(e) => {
                  setUserInitiated(true);
                  setIsEnabled(e.target.checked);
                }}
              />
              <Text ml={2}>{instanceConfig.enableLabel}</Text>
            </NavbarItem>
            <NavbarItem>
              {storeData.store_id ? (
                !storeData?.subscription ? (
                  <Button
                    variant="primary"
                    as="a"
                    href={getCheckoutUrl(storeData)}
                    target="_blank"
                  >
                    Start a trial
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    as="a"
                    href={
                      storeData.subscription?.management_urls
                        .update_payment_method
                    }
                    target="_blank"
                  >
                    Manage Billing Method
                  </Button>
                )
              ) : (
                <LoadingSpinner />
              )}
            </NavbarItem>
          </NavbarContent>
        </Navbar>
      }
    >
      <Box as="main" flex="1" py="2" px="4">
        <Container
          maxW="container.xl"
          px="4"
          display="flex"
          flexDirection="column"
          margin="0"
          pb="20" // Add bottom padding
        >
          <Form onSubmit={() => {}}>
            <Tabs isFitted variant="enclosed" defaultIndex={0}>
              <TabList>
                <Tab>Button Settings</Tab>
                <Tab>Popup Settings</Tab>
                <Tab>Other Settings</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <FormLayout>

                  {"People Management"}
                    <Accordion allowMultiple>
                      {config.popup.persons.map((person: any, idx: number) => (
                        <AccordionItem key={idx}>
                          <AccordionButton data-accordion-button>
                            <Box flex="1" textAlign="left">
                              <Text>
                                Person #{idx + 1}: {person.text.title}
                              </Text>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel>
                            <Stack spacing={6}>
                              <Stack>
                                <Heading size="sm">Basic Information</Heading>
                                <Field
                                  label="Name"
                                  name={`popup-text-title-${idx}`}
                                  value={person.text.title}
                                  onChange={(e: any) => {
                                    setUserInitiated(true);
                                    const updatedPersons = [
                                      ...config.popup.persons,
                                    ];
                                    updatedPersons[idx] = {
                                      ...updatedPersons[idx],
                                      text: {
                                        ...updatedPersons[idx].text,
                                        title: e.target.value,
                                      },
                                    };
                                    setConfig({
                                      ...config,
                                      popup: {
                                        ...config.popup,
                                        persons: updatedPersons,
                                      },
                                    });
                                  }}
                                />
                                <Field
                                  label="Description"
                                  name={`popup-text-description-${idx}`}
                                  value={person.text.description || ""}
                                  onChange={(e: any) => {
                                    setUserInitiated(true);
                                    const updatedPersons = [
                                      ...config.popup.persons,
                                    ];
                                    updatedPersons[idx] = {
                                      ...updatedPersons[idx],
                                      text: {
                                        ...updatedPersons[idx].text,
                                        description: e.target.value || false,
                                      },
                                    };
                                    setConfig({
                                      ...config,
                                      popup: {
                                        ...config.popup,
                                        persons: updatedPersons,
                                      },
                                    });
                                  }}
                                />
                              </Stack>
                              <Divider />
                              <Stack>
                                <Heading size="sm">Status Messages</Heading>
                                <Field
                                  label="Online Message"
                                  name={`popup-text-online-${idx}`}
                                  value={person.text.online || ""}
                                  onChange={(e: any) => {
                                    setUserInitiated(true);
                                    const updatedPersons = [
                                      ...config.popup.persons,
                                    ];
                                    updatedPersons[idx] = {
                                      ...updatedPersons[idx],
                                      text: {
                                        ...updatedPersons[idx].text,
                                        online: e.target.value || false,
                                      },
                                    };
                                    setConfig({
                                      ...config,
                                      popup: {
                                        ...config.popup,
                                        persons: updatedPersons,
                                      },
                                    });
                                  }}
                                />
                                <Field
                                  label="Offline Message"
                                  name={`popup-text-offline-${idx}`}
                                  value={person.text.offline || ""}
                                  onChange={(e: any) => {
                                    setUserInitiated(true);
                                    const updatedPersons = [
                                      ...config.popup.persons,
                                    ];
                                    updatedPersons[idx] = {
                                      ...updatedPersons[idx],
                                      text: {
                                        ...updatedPersons[idx].text,
                                        offline: e.target.value || false,
                                      },
                                    };
                                    setConfig({
                                      ...config,
                                      popup: {
                                        ...config.popup,
                                        persons: updatedPersons,
                                      },
                                    });
                                  }}
                                />
                              </Stack>
                              <Divider />
                              <Stack>
                                <Heading size="sm">WhatsApp Links</Heading>
                                <Field
                                  label="Desktop WhatsApp Link"
                                  name={`popup-link-desktop-${idx}`}
                                  value={person.link.desktop}
                                  onChange={(e: any) => {
                                    setUserInitiated(true);
                                    const updatedPersons = [
                                      ...config.popup.persons,
                                    ];
                                    updatedPersons[idx] = {
                                      ...updatedPersons[idx],
                                      link: {
                                        ...updatedPersons[idx].link,
                                        desktop: e.target.value,
                                      },
                                    };
                                    setConfig({
                                      ...config,
                                      popup: {
                                        ...config.popup,
                                        persons: updatedPersons,
                                      },
                                    });
                                  }}
                                />
                                <Field
                                  label="Mobile WhatsApp Link"
                                  name={`popup-link-mobile-${idx}`}
                                  value={person.link.mobile || ""}
                                  onChange={(e: any) => {
                                    setUserInitiated(true);
                                    const updatedPersons = [
                                      ...config.popup.persons,
                                    ];
                                    updatedPersons[idx] = {
                                      ...updatedPersons[idx],
                                      link: {
                                        ...updatedPersons[idx].link,
                                        mobile: e.target.value || false,
                                      },
                                    };
                                    setConfig({
                                      ...config,
                                      popup: {
                                        ...config.popup,
                                        persons: updatedPersons,
                                      },
                                    });
                                  }}
                                />
                              </Stack>
                              <Divider />
                              <Stack>
                                <Heading size="sm">Online Schedule</Heading>
                                <Stack direction="row" wrap="wrap" spacing={4}>
                                  {[
                                    "sunday",
                                    "monday",
                                    "tuesday",
                                    "wednesday",
                                    "thursday",
                                    "friday",
                                    "saturday",
                                  ].map((day) => (
                                    <Box key={day} flex="1" minW="200px">
                                      <Field
                                        label={
                                          day.charAt(0).toUpperCase() +
                                          day.slice(1)
                                        }
                                        name={`popup-onlineDay-${day}-${idx}`}
                                        value={person.onlineDay[day] || ""}
                                        placeholder="HH:MM-HH:MM or leave empty to disable"
                                        onChange={(e: any) => {
                                          setUserInitiated(true);
                                          const updatedPersons = [
                                            ...config.popup.persons,
                                          ];
                                          updatedPersons[idx] = {
                                            ...updatedPersons[idx],
                                            onlineDay: {
                                              ...updatedPersons[idx].onlineDay,
                                              [day]: e.target.value || false,
                                            },
                                          };
                                          setConfig({
                                            ...config,
                                            popup: {
                                              ...config.popup,
                                              persons: updatedPersons,
                                            },
                                          });
                                        }}
                                      />
                                    </Box>
                                  ))}
                                </Stack>
                              </Stack>
                              <Button
                                colorScheme="red"
                                onClick={() => {
                                  setUserInitiated(true);
                                  const updatedPersons = [
                                    ...config.popup.persons,
                                  ];
                                  updatedPersons.splice(idx, 1);
                                  setConfig({
                                    ...config,
                                    popup: {
                                      ...config.popup,
                                      persons: updatedPersons,
                                    },
                                  });
                                }}
                              >
                                Remove Person
                              </Button>
                            </Stack>
                          </AccordionPanel>
                        </AccordionItem>
                      ))}
                    </Accordion>
                    {"Button Position"}
                    <Select
                      name="button-position"
                      value={config.button.position}
                      options={[
                        { value: "left", label: "Left" },
                        { value: "right", label: "Right" },
                      ]}
                      onChange={(e) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          button: { ...config.button, position: e },
                        });
                      }}
                    >
                      <SelectButton />
                      <SelectList />
                    </Select>
                    {"Button Style"}
                    <Select
                      name="button-style"
                      value={`Style ${config.button.style}`}
                      options={[
                        { value: "1", label: "Style 1" },
                        { value: "2", label: "Style 2" },
                        { value: "3", label: "Style 3" },
                        { value: "4", label: "Style 4" },
                        { value: "5", label: "Style 5" },
                        { value: "6", label: "Style 6" },
                        { value: "7", label: "Style 7" },
                      ]}
                      onChange={(e) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          button: { ...config.button, style: Number(e) },
                        });
                      }}
                    >
                      <SelectButton />
                      <SelectList />
                    </Select>
                    {/* Button Background Color setting hidden */}
                    {"Button Effect"}
                    <Select
                      name="button-effect"
                      value={`Effect ${config.button.effect}`}
                      options={[
                        { value: "1", label: "Effect 1" },
                        { value: "2", label: "Effect 2" },
                        { value: "3", label: "Effect 3" },
                        { value: "4", label: "Effect 4" },
                        { value: "5", label: "Effect 5" },
                        { value: "6", label: "Effect 6" },
                        { value: "7", label: "Effect 7" },
                      ]}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          button: { ...config.button, effect: Number(e) },
                        })
                      }
                    >
                      <SelectButton />
                      <SelectList />
                    </Select>
                    {"Button Notification Number"}
                    <Field
                      name="button-notificationNumber"
                      value={config.button.notificationNumber}
                      type="number"
                      onChange={(e: any) => {
                        setConfig({
                          ...config,
                          button: {
                            ...config.button,
                            notificationNumber: e,
                          },
                        });
                      }}
                    />
                    {"Button Speech Bubble"}
                    <Field
                      name="button-speechBubble"
                      value={config.button.speechBubble}
                      onChange={(e: any) =>
                        setConfig({
                          ...config,
                          button: {
                            ...config.button,
                            speechBubble: e.target.value,
                          },
                        })
                      }
                    />
                    {"Button Pulse Effect"}
                    <Field
                      name="button-pulseEffect"
                      value={config.button.pulseEffect}
                      type="checkbox"
                      label="Pulse Effect"
                      onChange={(e: any) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          button: {
                            ...config.button,
                            pulseEffect: e.target.value,
                          },
                        });
                      }}
                    />
                    {"Button Title"}
                    <Field
                      name="button-title"
                      value={config.button.text.title}
                      onChange={(e: any) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          button: {
                            ...config.button,
                            text: {
                              ...config.button.text,
                              title: e.target.value,
                            },
                          },
                        });
                      }}
                    />
                    {"Button Description"}
                    <Field
                      name="button-description"
                      value={config.button.text.description}
                      onChange={(e: any) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          button: {
                            ...config.button,
                            text: {
                              ...config.button.text,
                              description: e.target.value,
                            },
                          },
                        });
                      }}
                    />
                    {"Button Online"}
                    <Field
                      name="button-online"
                      value={config.button.text.online}
                      onChange={(e: any) =>
                        setConfig({
                          ...config,
                          button: {
                            ...config.button,
                            text: {
                              ...config.button.text,
                              online: e.target.value,
                            },
                          },
                        })
                      }
                    />
                    {"Button Offline"}
                    <Field
                      name="button-offline"
                      value={config.button.text.offline}
                      onChange={(e: any) =>
                        setConfig({
                          ...config,
                          button: {
                            ...config.button,
                            text: {
                              ...config.button.text,
                              offline: e.target.value,
                            },
                          },
                        })
                      }
                    />
                  </FormLayout>
                </TabPanel>
                <TabPanel>
                  <FormLayout>
                    {"Popup Automatic Open"}
                    <Field
                      name="popup-automaticOpen"
                      value={config.popup.automaticOpen}
                      type="checkbox"
                      label="Automatic Open"
                      onChange={(e: any) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          popup: {
                            ...config.popup,
                            automaticOpen: e.target.value,
                          },
                        });
                      }}
                    />
                    {"Popup Outside Click Close Popup"}
                    <Field
                      name="popup-outsideClickClosePopup"
                      value={config.popup.outsideClickClosePopup}
                      type="checkbox"
                      label="Outside Click Close Popup"
                      onChange={(e: any) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          popup: {
                            ...config.popup,
                            outsideClickClosePopup: e.target.value,
                          },
                        });
                      }}
                    />
                    {"Popup Effect"}
                    <Select
                      name="popup-effect"
                      value={`Effect ${config.popup.effect}`}
                      options={[
                        { value: "1", label: "Effect 1" },
                        { value: "2", label: "Effect 2" },
                        { value: "3", label: "Effect 3" },
                        { value: "4", label: "Effect 4" },
                        { value: "5", label: "Effect 5" },
                        { value: "6", label: "Effect 6" },
                        { value: "7", label: "Effect 7" },
                        { value: "8", label: "Effect 8" },
                        { value: "9", label: "Effect 9" },
                        { value: "10", label: "Effect 10" },
                        { value: "11", label: "Effect 11" },
                        { value: "12", label: "Effect 12" },
                        { value: "13", label: "Effect 13" },
                        { value: "14", label: "Effect 14" },
                        { value: "15", label: "Effect 15" },
                      ]}
                      onChange={(e) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          popup: { ...config.popup, effect: Number(e) },
                        });
                      }}
                    >
                      <SelectButton />
                      <SelectList />
                    </Select>
                    {/* Popup Header Background Color setting hidden */}
                    {"Popup Header Title"}
                    <Field
                      name="popup-header-title"
                      value={config.popup.header.title}
                      onChange={(e: any) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          popup: {
                            ...config.popup,
                            header: {
                              ...config.popup.header,
                              title: e.target.value,
                            },
                          },
                        });
                      }}
                    />
                    {"Popup Header Description"}
                    <Field
                      name="popup-header-description"
                      value={config.popup.header.description}
                      onChange={(e: any) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          popup: {
                            ...config.popup,
                            header: {
                              ...config.popup.header,
                              description: e.target.value,
                            },
                          },
                        });
                      }}
                    />
                    {"Popup Avatar Source"}
                    <FileUploader
                      onUploadComplete={(htmlImgTag: string) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          popup: {
                            ...config.popup,
                            persons: [
                              {
                                ...config.popup.persons[0],
                                avatar: {
                                  ...config.popup.persons[0].avatar,
                                  src: htmlImgTag,
                                },
                              },
                            ],
                          },
                        });
                      }}
                    />
                    {"Popup Avatar Background Color"}
                    <Field
                      name="popup-avatar-backgroudColor"
                      value={config.popup.persons[0].avatar.backgroudColor}
                      onChange={(e: any) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          popup: {
                            ...config.popup,
                            persons: [
                              {
                                ...config.popup.persons[0],
                                avatar: {
                                  ...config.popup.persons[0].avatar,
                                  backgroudColor: e.target.value,
                                },
                              },
                            ],
                          },
                        });
                      }}
                    />
                    {"Popup Avatar Online Circle"}
                    <Field
                      name="popup-avatar-onlineCircle"
                      value={config.popup.persons[0].avatar.onlineCircle}
                      type="checkbox"
                      label="Online Circle"
                      onChange={(e: any) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          popup: {
                            ...config.popup,
                            persons: [
                              {
                                ...config.popup.persons[0],
                                avatar: {
                                  ...config.popup.persons[0].avatar,
                                  onlineCircle: e.target.value,
                                },
                              },
                            ],
                          },
                        });
                      }}
                    />
                    <Box mt={4}>
                      <Text mb={2}>{"Add New Person"}</Text>
                      <Button
                        onClick={() => {
                          if (config.popup.persons.length >= 1 && !storeData?.subscription) {
                            setIsUpgradeModalOpen(true);
                            return;
                          }
                          
                          setUserInitiated(true);
                          setConfig({
                            ...config,
                            popup: {
                              ...config.popup,
                              persons: [
                                ...config.popup.persons,
                                {
                                  avatar: {
                                    src: '<img src="assets/img/person/1.jpg" alt="">' /* Image, Icon or SVG */,
                                    backgroudColor:
                                      "#ffffff" /* Html color code */,
                                    onlineCircle:
                                      true /* Avatar online circle. To remove, (onlineCircle:false) */,
                                  },
                                  text: {
                                    title:
                                      "New Team Member" /* Writing is required */,
                                    description:
                                      "Sales Support" /* To remove, (description:false) */,
                                    online:
                                      "I'm Online" /* To remove, (online:false) */,
                                    offline:
                                      "I will be back soon" /* To remove, (offline:false) */,
                                  },
                                  link: {
                                    desktop:
                                      "https://web.whatsapp.com/send?phone=13475086396&text=Hi" /* Writing is required */,
                                    mobile:
                                      "https://wa.me/13475086396/?text=Hi" /* If it is hidden desktop link will be valid. To remove, (mobile:false) */,
                                  },
                                  onlineDay: {
                                    /* Change the day you are offline like this. (sunday:false) */
                                    sunday: "00:00-23:59",
                                    monday: "00:00-23:59",
                                    tuesday: "00:00-23:59",
                                    wednesday: "00:00-23:59",
                                    thursday: "00:00-23:59",
                                    friday: "00:00-23:59",
                                    saturday: "00:00-23:59",
                                  },
                                },
                              ],
                            },
                          });
                        }}
                      >
                        Add Person
                      </Button>
                    </Box>
                  </FormLayout>
                </TabPanel>
                <TabPanel>
                  <FormLayout>
                    {"Sound"}
                    <Field
                      name="sound"
                      value={config.sound}
                      type="checkbox"
                      label="Enable Sound"
                      onChange={(e: any) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          sound: e.target.value,
                        });
                      }}
                    />
                    {"Change Browser Title"}
                    <Field
                      name="changeBrowserTitle"
                      value={config.changeBrowserTitle}
                      onChange={(e: any) => {
                        setUserInitiated(true);
                        setConfig({
                          ...config,
                          changeBrowserTitle: e.target.value,
                        });
                      }}
                    />
                  </FormLayout>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Form>
          
          {/* Add upgrade modal */}
          <Modal isOpen={isUpgradeModalOpen} onClose={() => setIsUpgradeModalOpen(false)}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Upgrade Required</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text>
                  To add more than one person, you need to upgrade to our paid plan.
                </Text>
              </ModalBody>
              <ModalFooter>
                <Button
                  colorScheme="blue"
                  mr={3}
                  as="a"
                  href={getCheckoutUrl(storeData)}
                  target="_blank"
                >
                  Upgrade Now
                </Button>
                <Button variant="ghost" onClick={() => setIsUpgradeModalOpen(false)}>
                  Cancel
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Container>
      </Box>
    </AppShell>
  );
}

export default App;
